'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { LocationHydration } from 'stores/LocationStore';
import { Authentication } from 'components/Session';
import { useScroll } from 'hooks/useScroll';
import IconSymplaMinimal from 'assets/icons/logo-sympla-minimal.svg';
import { isMobile as getIsMobile } from 'utils/misc';
import IconSympla from 'assets/icons/logo-sympla.svg';
import NewHeaderContext from '../NewHeaderContext/NewHeaderContext';
import { MobileCTACreateEventBanner } from '../MobileCTACreateEventBanner/MobileCTACreateEventBanner';
import LocationButton from '../LocationButton/LocationButton';
import * as styles from './Navbar.css';
import classNames from 'classnames';

interface NavbarProps {
  userId: number | null;
  locationHydration: LocationHydration;
  alwaysCollapsed?: boolean;
  isMobile?: boolean;
  isDark?: boolean;
  hasMossColor?: boolean;
}

const minHeight = {
  navbarCollapsedDesktop: 92,
  baseDesktop: 232,
  baseMobile: 212
};

function Navbar({
  userId,
  locationHydration,
  alwaysCollapsed = false,
  isMobile = false,
  isDark = false,
  hasMossColor = false
}: NavbarProps) {
  const isNavbarCollapsed = useScroll(alwaysCollapsed);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const paddingInitial = useMemo(() => {
    if (isMobile) return minHeight.baseMobile;
    return isNavbarCollapsed
      ? minHeight.navbarCollapsedDesktop
      : minHeight.baseDesktop;
  }, [isNavbarCollapsed, isMobile]);
  const [navPadding, setNavPadding] = useState(paddingInitial);
  const headerElement = headerRef.current;

  const updateHeight = useCallback(() => {
    const heightElement = headerRef.current?.offsetHeight || paddingInitial;
    setNavPadding(heightElement);
  }, [paddingInitial]);

  useEffect(() => {
    if (headerElement) updateHeight();
  }, [isNavbarCollapsed, isMobile]);

  useEffect(() => {
    const isMobileWindow = getIsMobile();
    if (isMobileWindow !== isMobile && isNavbarCollapsed) updateHeight();
  }, []);

  const logoClassName = isDark ? styles.LogoStyleDark : styles.LogoStyle;
  const containerClass = classNames(styles.container, {
    [styles.containerCollapsed]: isNavbarCollapsed,
    [styles.containerDark]: isDark
  });

  const containerClassMobile = classNames(
    styles.container,
    styles.filtersContainer,
    styles.containerMobile,
    {
      [styles.containerCollapsed]: isNavbarCollapsed,
      [styles.containerDark]: isDark
    }
  );

  return (
    <header className={`${isDark ? styles.containerDark : ''}`}>
      <nav
        ref={headerRef}
        className={`${styles.navbar} ${isDark ? styles.containerDark : ''}`}
        onTransitionEnd={updateHeight}
      >
        <MobileCTACreateEventBanner
          isMobile={isMobile}
          handleCloseBanner={updateHeight}
          isDark={isDark}
        />

        <div className={containerClass}>
          <Link
            href="/"
            aria-label="Sympla sua plataforma de eventos. Logo."
            title="Sympla sua plataforma de eventos"
            className={styles.LinkLogo}
            data-testid="header-navbar-logo"
          >
            <div className={styles.containerMobile}>
              <IconSymplaMinimal
                width={32}
                height={32}
                className={logoClassName}
                alt="Sympla sua plataforma de eventos"
              />
            </div>
            <div className={styles.containerDesktop}>
              <div hidden={!isNavbarCollapsed}>
                <IconSymplaMinimal
                  width={38}
                  height={38}
                  className={logoClassName}
                  alt="Sympla sua plataforma de eventos"
                  hidden={!isNavbarCollapsed}
                />
              </div>
              <div hidden={isNavbarCollapsed}>
                <IconSympla
                  width={112}
                  height={48}
                  className={`${styles.containerDesktop} ${logoClassName}`}
                  alt="Sympla sua plataforma de eventos"
                  hidden={isNavbarCollapsed}
                />
              </div>
            </div>
          </Link>

          <div className={styles.containerMobile}>
            <LocationButton
              selectedLocation={locationHydration.selectedLocation}
              variant="default"
            />
          </div>

          <div
            className={`${styles.filtersContainer} ${
              isNavbarCollapsed ? styles.filtersContainerCollapsed : ''
            } ${styles.containerDesktop}`}
            data-testid="header-navbar-search-desktop"
          >
            <NewHeaderContext
              locationHydration={locationHydration}
              isNavbarCollapsed={isNavbarCollapsed}
              variant={isDark ? 'dark' : 'primary'}
              isDark={isDark}
              hasMossColor={hasMossColor}
            />
          </div>

          <div style={{ zIndex: 1 }} data-testid="header-navbar-auth">
            <Authentication
              isAuthenticated={!!userId}
              isNavbarCollapsed={isNavbarCollapsed}
              isDark={isDark}
              hasMossColor={hasMossColor}
            />
          </div>
        </div>

        <div
          className={containerClassMobile}
          data-testid="header-navbar-search-mobile"
        >
          <NewHeaderContext
            locationHydration={locationHydration}
            isNavbarCollapsed={isNavbarCollapsed}
            isDark={isDark}
            hasMossColor={hasMossColor}
          />
        </div>
      </nav>

      <div style={{ paddingTop: `${navPadding}px`, transition: 'all .3s' }} />
    </header>
  );
}

export default Navbar;
